import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { IsfStateService } from '../../../services';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { debounceTime, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { EntityModel } from '@data-layer/models/entities';
import { InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { IsfV1EntityHtsModel } from '@data-layer/models/isf-v1';
import { CustomValidator } from '@helpers/custom-validator';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { FormHelper } from '@helpers/form.helper';
import { MissingFieldsComponent } from '@shared/missing-fields/components';

@Component({
  selector: 'desktop-shipment-details-hts',
  template: `
    <p [ngClass]="{ sidebar: isSideBar }">{{ 'INFO.HTS.SUB_TITLE_DESKTOP' | translate }}</p>
    <span [ngClass]="{ sidebar: isSideBar }" [innerHTML]="'INFO.HTS.SUB_TITLE' | translate"></span>

    <form [formGroup]="formGroup" *ngIf="manufacturers?.length; else cardAttention" autocomplete="off">
      <div formArrayName="manufacturers">
        <div *ngFor="let manufacturerFormGroup of manufacturersFormGroup; let i = index">
          <app-hts-mf
            [manufacturerFormGroup]="manufacturerFormGroup"
            [isDesktop]="true"
            [disableMFEdit]="disableMFEdit"
            [index]="i"
            (editMF)="editMF.emit({ id: manufacturers[i]?.id, entityType: enumEntityModelCode.MANUFACTURER })"
          ></app-hts-mf>
        </div>
      </div>

      <div class="hts-form-buttons" *ngIf="isSideBar">
        <button type="submit" class="btn btn-primary m-auto" (click)="saveOnButtonClick()">
          {{ 'BUTTON.SAVE' | translate }}
        </button>
      </div>
    </form>

    <ng-template #cardAttention>
      <div class="hts-manufacturer-warning">
        {{ 'STATUS.CARD_TEXT.HTS_WITHOUT_MANUFACTURE' | translate : { code: 'ISF' } }}
      </div>
    </ng-template>

    <missing-fields [code]="'LABELS.INTERNATIONAL_HTS_CODE'" (decline)="saveOnButtonClick(false)"></missing-fields>
  `,
  styleUrls: ['./desktop-shipment-details-hts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopShipmentDetailsHtsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public manufacturers: EntityModel[] = [];
  @Input() public invoices: InvoiceModel[] = [];
  @Input() public isSideBar: boolean;
  @Input() public isSaveOnParentBtn: boolean;
  @Input() public disableMFEdit: boolean;
  @Output() public editMF: EventEmitter<{ id: number; entityType: enumEntityModelCode }> = new EventEmitter<{
    id: number;
    entityType: enumEntityModelCode;
  }>();
  @Output() public dataSaved: EventEmitter<void> = new EventEmitter<void>();
  @Output() public openValidationModal: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() public formValidityUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldsComponent: MissingFieldsComponent;

  public formGroup: UntypedFormGroup;
  public manufacturersFormGroup: AbstractControl[];
  public errors = {
    required: 'FORM_ERROR.REQUIRED',
  };
  public initialValue: IsfV1EntityHtsModel[] = [];
  public enumEntityModelCode = enumEntityModelCode;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(private isfStateService: IsfStateService, private formBuilder: UntypedFormBuilder, private fh: FormHelper) {}

  public ngOnInit(): void {
    this.initFormGroup();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.manufacturers && !changes.manufacturers.firstChange) {
      if (changes.manufacturers.currentValue.length !== changes.manufacturers.previousValue.length) {
        this.initFormGroup();
      } else {
        changes.manufacturers.currentValue.forEach((item) => {
          const value = this.formGroup.get('manufacturers').value;
          const index = value.findIndex((invoice) => invoice.mID === item.id);
          (this.formGroup.get('manufacturers') as UntypedFormArray).controls[index].patchValue(
            { mName: item?.name, mAddress: item?.address },
            { emitEvent: false },
          );
        });
      }
    }

    if (changes.invoices && !changes.invoices.firstChange) {
      this.initFormGroup();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public saveHTS(): Observable<any> {
    return this.isfStateService.saveHTS(this.getFormHTSValue(), this.invoices, this.initialValue);
  }

  public saveOnButtonClick(needValidate: boolean = true): void {
    if (needValidate) {
      this.validateForm();
    }

    if (this.formGroup.valid || !needValidate) {
      this.saveHTS()
        .pipe(take(1))
        .subscribe(() => {
          this.dataSaved.emit();
        });
    }
  }

  private initialForm(): void {
    const manufacturerListFormGroup = this.invoices.map((invoice) => this.createManufacturerGroup(invoice));

    manufacturerListFormGroup.forEach((gr) => {
      (<UntypedFormArray>this.formGroup.get('manufacturers')).push(gr);
    });
  }

  private createManufacturerGroup(invoice: InvoiceModel) {
    const htsFormArray = !invoice.lines ? [] : invoice.lines.map((hts: LineModel) => this.createHtsGroup(hts));

    return this.formBuilder.group({
      invoiceId: new UntypedFormControl(invoice.id),
      mID: new UntypedFormControl(invoice.manufacturerId),
      mName: new UntypedFormControl(invoice.manufacturer?.name),
      mAddress: new UntypedFormControl(invoice.manufacturer?.address),
      hts: new UntypedFormArray(htsFormArray),
    });
  }

  private createHtsGroup(lineItem: LineModel): UntypedFormGroup {
    return this.formBuilder.group({
      id: this.formBuilder.control(lineItem.id || 0),
      number: this.formBuilder.control(lineItem.htsNumber || null, { updateOn: 'blur' }),
      countryOfOrigin: this.formBuilder.control(lineItem?.countryOfOrigin?.code || null),
      countryOfOriginId: this.formBuilder.control(
        lineItem?.countryOfOrigin?.id || null,
        CustomValidator.requiredHTSCountryOfOrigin('number'),
      ),
      countryName: this.formBuilder.control(lineItem?.countryOfOrigin?.description || null),
    });
  }

  private getFormHTSValue(): IsfV1EntityHtsModel[] {
    return this.isfStateService.getFormHTSValue(this.formGroup.value);
  }

  private saveOnFormUpdate(): void {
    this.formGroup.valueChanges
      .pipe(
        skip(1),
        debounceTime(100),
        switchMap(() => {
          if (this.formGroup.valid) {
            return this.saveHTS();
          } else {
            return of(true);
          }
        }),
        takeUntil(this.destroy$),
        tap(() => {
          if (this.formGroup.valid) {
            this.dataSaved.emit();
          }
        }),
      )
      .subscribe();
  }

  private initFormGroup(updateSubscr: boolean = true): void {
    this.manufacturers = [...this.manufacturers];
    this.invoices = [...this.invoices];
    this.formGroup = this.formBuilder.group({
      manufacturers: new UntypedFormArray([]),
    });
    this.manufacturersFormGroup = (this.formGroup.get('manufacturers') as UntypedFormArray)?.controls;
    this.initialForm();
    this.initialValue = [...this.getFormHTSValue()];
    if (!this.isSideBar && updateSubscr && !this.isSaveOnParentBtn) {
      this.saveOnFormUpdate();
    }

    this.formGroup.valueChanges
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.formValidityUpdated.emit(this.formGroup.valid));
  }

  private validateForm(): void {
    let invalidFields = this.fh.validateAllFields(this.formGroup);
    const htsNumberKey = 'htsNumber';

    if (Object.keys(invalidFields).length === 0) {
      // check for existing code, do not take into account last element
      if (
        this.formGroup.value.manufacturers.some((item) => {
          const sliceIndex = item.hts.length > 0 ? -1 : item.hts.length;

          item.hts.slice(0, sliceIndex).some((htsItem) => !htsItem.number);
        })
      ) {
        invalidFields = { [htsNumberKey]: htsNumberKey };
      }
      if (
        this.formGroup.value.manufacturers.some((item) => {
          const sliceIndex = item.hts.length > 0 ? -1 : item.hts.length;

          item.hts.slice(0, sliceIndex).some((htsItem) => !htsItem.number);
        })
      ) {
        invalidFields = { ...invalidFields, countryOfOriginId: 'countryOfOriginId' };
      }
    }

    // introduce hts code validation
    if (invalidFields.hasOwnProperty('number')) {
      delete invalidFields['number'];
      invalidFields[htsNumberKey] = htsNumberKey;
    }

    if (Object.keys(invalidFields).length) {
      if (this.isSaveOnParentBtn) {
        this.openValidationModal.emit(invalidFields);
      } else {
        this.missingFieldsComponent.openCustomModal(invalidFields);
      }
    }
  }
}
